import React from 'react';
import { Pagination, Tooltip } from '@mui/material';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CustomButton from '../../../components/buttons/CustomButton';
import ResourceRow from './ResourceRow';
import ResourceListSkeleton from './ResourceListSkeleton';
import styles from './ResourceListCard.module.css';

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

/** Estado vazio: o que é uma agenda e o atalho para criar a primeira. */
function EmptyAgendas({ addBlockedReason, canManage, onAdd }) {
    return (
        <div className={styles.empty}>
            <span className={styles.emptyArt} aria-hidden="true">
                <span className={styles.emptyArtBack} />
                <span className={styles.emptyArtIcon}><EventAvailableOutlinedIcon /></span>
            </span>
            <span className={styles.emptyTitle}>Nenhuma agenda ainda</span>
            <span className={styles.emptyText}>
                Crie uma agenda para cada profissional da equipe. Ela vira uma coluna na tela de marcação.
            </span>
            <Tooltip title={addBlockedReason} arrow>
                <span>
                    <CustomButton
                        style="main"
                        variant="contained"
                        icon={<AddRoundedIcon />}
                        label="Criar primeira agenda"
                        onClick={onAdd}
                        enabled={canManage}
                    />
                </span>
            </Tooltip>
        </div>
    );
}

/**
 * Lista das agendas: contagem de ativas e inativas no cabeçalho, uma linha por agenda,
 * carregamento, estado vazio e paginação quando passa de uma página.
 */
function ResourceListCard({
    loading, items, activeCount, totalCount, currentPage, totalPages, onPageChange,
    getSignatureSrc, getDefaultRoomLabel, canManage, addBlockedReason, reactivateHint, onAdd, onEdit, onInactivate, onReactivate,
}) {
    const inactiveCount = totalCount - activeCount;

    return (
        <section className={styles.card}>
            <header className={styles.header}>
                <div className={styles.headerText}>
                    <h3 className={styles.title}>Agendas da equipe</h3>
                    {totalCount > 0 && (
                        <span className={styles.subtitle}>
                            {countLabel(activeCount, 'ativa', 'ativas')}
                            {inactiveCount > 0 ? ` · ${countLabel(inactiveCount, 'inativa', 'inativas')}` : ''}
                        </span>
                    )}
                </div>
            </header>

            {loading ? (
                <ResourceListSkeleton />
            ) : totalCount === 0 ? (
                <EmptyAgendas addBlockedReason={addBlockedReason} canManage={canManage} onAdd={onAdd} />
            ) : (
                <ul className={styles.list}>
                    {items.map((resource) => (
                        <ResourceRow
                            key={resource.Id}
                            resource={resource}
                            signatureSrc={getSignatureSrc(resource)}
                            defaultRoomLabel={getDefaultRoomLabel ? getDefaultRoomLabel(resource) : ''}
                            canManage={canManage}
                            reactivateHint={reactivateHint}
                            onEdit={onEdit}
                            onInactivate={onInactivate}
                            onReactivate={onReactivate}
                        />
                    ))}
                </ul>
            )}

            {!loading && totalPages > 1 && (
                <footer className={styles.footer}>
                    <span className={styles.pageText}>Página {currentPage} de {totalPages}</span>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={onPageChange}
                        color="primary"
                        shape="rounded"
                        size="small"
                    />
                </footer>
            )}
        </section>
    );
}

export default ResourceListCard;
